// Bilingual dictionary for the ITM Consulting marketing site.
// EN is the default; RU is a manual, precise translation aimed at the same
// professional B2B audience (ANSPs, integrators, technology vendors).

export const translations = {
  en: {
    meta: {
      langLabel: "EN",
      switchTo: "Switch to Russian",
    },
    nav: {
      about: "About",
      services: "Services",
      capabilities: "Capabilities",
      engineering: "Engineering",
      trackRecord: "Track Record",
      contact: "Contact",
      cta: "Discuss your program",
    },
    hero: {
      eyebrow: "Independent ATM & CNS Practice",
      title: "Air Traffic Management & CNS Advisory, Program Delivery, and Technical Support.",
      subtitle:
        "Independent consulting for air navigation service providers, integrators and technology vendors — from strategy and procurement to program delivery and equipment support.",
      primaryCta: "Discuss your program",
      secondaryCta: "Explore services",
      stats: [
        { value: "20+", label: "Years across IT, telecom & civil aviation" },
        { value: "IPMA", label: "Certified program management" },
        { value: "CIS", label: "Regional focus, global vendor network" },
      ],
    },
    about: {
      eyebrow: "About",
      title: "An independent advisory and program-delivery practice for ATM and CNS.",
      paragraphs: [
        "ITM Consulting is an independent advisory and program-delivery practice focused on Air Traffic Management (ATM) and Communication, Navigation & Surveillance (CNS) systems.",
        "The practice is led by an IPMA-certified program manager with 20+ years of experience across IT, telecommunications and civil aviation, backed by an in-house engineering team.",
        "We work across ANSPs, system integrators and technology vendors — bridging the commercial, technical and delivery layers of complex, safety-critical programs.",
      ],
      pillars: [
        { title: "Independent", body: "Vendor-neutral, technology-agnostic recommendations." },
        { title: "End-to-end", body: "Strategy, procurement, delivery and lifecycle support." },
        { title: "Engineering-backed", body: "In-house team for repair, servicing and diagnostics." },
      ],
    },
    services: {
      eyebrow: "Services",
      title: "From strategy to delivery — and everything in between.",
      subtitle:
        "Seven focused engagements, deployed individually or as one continuous program.",
      items: [
        {
          tag: "A",
          title: "ATM / CNS Advisory & Strategy",
          body: "Architecture reviews, technology selection and modernization roadmaps for surveillance, communications, navigation and ATC automation.",
        },
        {
          tag: "B",
          title: "Program & Project Management",
          body: "IPMA-certified, end-to-end management of complex ATM programs — scope, schedule, cost, risk and stakeholder governance.",
        },
        {
          tag: "C",
          title: "Procurement & Vendor Selection",
          body: "Tender preparation, vendor evaluation and contract negotiation for surveillance, communications and ATC automation systems.",
        },
        {
          tag: "D",
          title: "Systems Integration Oversight",
          body: "Technical oversight of integrators and suppliers to keep delivery aligned with ICAO / Eurocontrol standards and safety requirements.",
        },
        {
          tag: "E",
          title: "Vendor & Partner Coordination",
          body: "Established working relationships with ATM supply-chain partners across surveillance, comms, navigation and automation.",
        },
        {
          tag: "F",
          title: "ATM Equipment Repair & Maintenance",
          body: "In-house engineering for hands-on repair, servicing, diagnostics and lifecycle support of CNS, navigation and surveillance equipment — current-generation and legacy.",
        },
        {
          tag: "G",
          title: "Business Development & Market Entry",
          body: "Structured market-entry support for technology vendors targeting CIS and regional aviation markets.",
        },
      ],
    },
    capabilities: {
      eyebrow: "Capabilities",
      title: "Deep coverage across the CNS and ATM technology stack.",
      subtitle:
        "The domains we deliver on every day — from radar and datalink to certification and safety-critical program governance.",
      tags: [
        "Surveillance — ADS-B",
        "Surveillance — PSR / SSR",
        "Surveillance — MLAT",
        "Communications — VHF",
        "Communications — AMHS / Voice",
        "Navigation systems",
        "ATC automation — FDP / RDP",
        "CNS infrastructure lifecycle",
        "ICAO / Eurocontrol standards",
        "Safety-critical program delivery",
        "Tender & procurement management",
        "Systems integration oversight",
      ],
    },
    engineering: {
      eyebrow: "Engineering",
      title: "An in-house engineering team, not a subcontracted layer.",
      paragraphs: [
        "Our engineering team performs hands-on repair, servicing, maintenance and diagnostics of ATM equipment — CNS, navigation and surveillance systems.",
        "We support current-generation and legacy installations, focusing on minimizing downtime and extending the operational life of critical assets.",
      ],
      bullets: [
        "Hands-on repair, servicing and diagnostics",
        "Lifecycle support for CNS, navigation and surveillance",
        "Coverage of current-generation and legacy installations",
        "Downtime reduction and operational life extension",
      ],
    },
    track: {
      eyebrow: "Track Record",
      title: "Facts and figures. No filler.",
      items: [
        {
          value: "Republic-scale",
          title: "National ATM system program",
          body: "Program Manager role — from contract structuring through to delivery. Customer name undisclosed.",
        },
        {
          value: "20+ yrs",
          title: "Cross-domain experience",
          body: "IT, telecommunications and civil aviation, with delivery exposure to global ATM vendors and integrators.",
        },
        {
          value: "$3M → $24M",
          title: "Systems-integration business grown",
          body: "Helped grow a client's systems-integration business from $3M to $24M, later ranked by Forbes (2019) among top IT companies.",
        },
        {
          value: "IPMA",
          title: "Certified program management",
          body: "Governance and delivery discipline aligned with international program-management standards.",
        },
      ],
    },
    contact: {
      eyebrow: "Contact",
      title: "Let's talk about your program.",
      subtitle:
        "For procurement questions, program reviews or engineering support — reach out directly.",
      company: "ITM Consulting",
      location: "Astana, Yesil District, E 652 Street, Building 10, Premises 3",
      emailLabel: "Email",
      email: "pm@itmcon.co",
      whatsappLabel: "WhatsApp",
      whatsappCta: "Message on WhatsApp",
      whatsappUrl: "https://wa.me/77773331234",
      note: "Client engagements are typically bound by NDA. Vendor and partner names are shared on request.",
    },
    footer: {
      tagline: "Independent ATM & CNS practice.",
      rights: "All rights reserved.",
      builtIn: "Based in Astana, Kazakhstan.",
    },
  },

  ru: {
    meta: {
      langLabel: "RU",
      switchTo: "Переключить на английский",
    },
    nav: {
      about: "О компании",
      services: "Услуги",
      capabilities: "Компетенции",
      engineering: "Инженерия",
      trackRecord: "Опыт",
      contact: "Контакты",
      cta: "Обсудить программу",
    },
    hero: {
      eyebrow: "Независимая практика ATM и CNS",
      title: "Консалтинг, управление программами и инженерная поддержка ATM и CNS.",
      subtitle:
        "Независимый консалтинг для провайдеров аэронавигационного обслуживания, интеграторов и производителей технологий — от стратегии и закупок до реализации программ и поддержки оборудования.",
      primaryCta: "Обсудить программу",
      secondaryCta: "Смотреть услуги",
      stats: [
        { value: "20+", label: "Лет в ИТ, телеком и гражданской авиации" },
        { value: "IPMA", label: "Сертифицированное управление программами" },
        { value: "СНГ", label: "Региональный фокус, глобальная сеть вендоров" },
      ],
    },
    about: {
      eyebrow: "О компании",
      title: "Независимая консалтинговая и проектная практика в области ATM и CNS.",
      paragraphs: [
        "ITM Consulting — независимая консалтинговая компания, специализирующаяся на управлении воздушным движением (ATM) и системах связи, навигации и наблюдения (CNS), с полным циклом сопровождения программ — от консалтинга до реализации.",
        "Практикой руководит IPMA-сертифицированный программный менеджер с 20+ годами опыта в ИТ, телекоммуникациях и гражданской авиации. За ним стоит собственная инженерная команда.",
        "Мы работаем с ANSP, системными интеграторами и производителями оборудования — соединяя коммерческий, технический и проектный слои сложных программ, критичных к безопасности.",
      ],
      pillars: [
        { title: "Независимость", body: "Нейтральные, вендоронезависимые рекомендации." },
        { title: "От А до Я", body: "Стратегия, закупки, реализация и поддержка жизненного цикла." },
        { title: "Инженерия внутри", body: "Собственная команда: ремонт, обслуживание, диагностика." },
      ],
    },
    services: {
      eyebrow: "Услуги",
      title: "От стратегии до внедрения — и всё, что между ними.",
      subtitle:
        "Семь направлений, применяемых отдельно или как единая программа.",
      items: [
        {
          tag: "A",
          title: "Консалтинг и стратегия ATM / CNS",
          body: "Аудит архитектуры, выбор технологий и дорожные карты модернизации для наблюдения, связи, навигации и автоматизации УВД.",
        },
        {
          tag: "B",
          title: "Управление программами и проектами",
          body: "IPMA-сертифицированное сквозное управление сложными ATM-программами: объём, сроки, стоимость, риски и работа со стейкхолдерами.",
        },
        {
          tag: "C",
          title: "Закупки и выбор вендоров",
          body: "Подготовка тендеров, оценка поставщиков и переговоры по контрактам для систем наблюдения, связи и автоматизации УВД.",
        },
        {
          tag: "D",
          title: "Контроль системной интеграции",
          body: "Технический надзор за интеграторами и поставщиками — соответствие стандартам ICAO / Eurocontrol и требованиям безопасности.",
        },
        {
          tag: "E",
          title: "Координация с вендорами и партнёрами",
          body: "Устойчивые рабочие отношения с игроками цепочки поставок ATM — наблюдение, связь, навигация, автоматизация.",
        },
        {
          tag: "F",
          title: "Ремонт и обслуживание ATM-оборудования",
          body: "Собственная инженерная команда: ремонт, обслуживание, диагностика и поддержка жизненного цикла CNS, навигации и систем наблюдения — как современных, так и устаревших.",
        },
        {
          tag: "G",
          title: "Развитие бизнеса и выход на рынок",
          body: "Структурированная поддержка выхода технологических вендоров на авиационные рынки СНГ и региона.",
        },
      ],
    },
    capabilities: {
      eyebrow: "Компетенции",
      title: "Полное покрытие стека технологий CNS и ATM.",
      subtitle:
        "Домены, в которых мы работаем ежедневно — от радара и линий передачи данных до сертификации и управления программами, критичными к безопасности.",
      tags: [
        "Наблюдение — ADS-B",
        "Наблюдение — PSR / SSR",
        "Наблюдение — MLAT",
        "Связь — VHF",
        "Связь — AMHS / голос",
        "Навигационные системы",
        "Автоматизация УВД — FDP / RDP",
        "Жизненный цикл инфраструктуры CNS",
        "Стандарты ICAO / Eurocontrol",
        "Программы, критичные к безопасности",
        "Управление тендерами и закупками",
        "Надзор за системной интеграцией",
      ],
    },
    engineering: {
      eyebrow: "Инженерия",
      title: "Собственная инженерная команда, а не субподрядный слой.",
      paragraphs: [
        "Наша инженерная команда выполняет ремонт, обслуживание и диагностику ATM-оборудования — систем CNS, навигации и наблюдения.",
        "Мы поддерживаем как современные, так и устаревшие установки, минимизируя простои и продлевая срок службы критичных активов.",
      ],
      bullets: [
        "Ремонт, обслуживание и диагностика на объекте",
        "Поддержка жизненного цикла CNS, навигации и наблюдения",
        "Покрытие современных и устаревших установок",
        "Снижение простоев и продление срока службы",
      ],
    },
    track: {
      eyebrow: "Опыт",
      title: "Только факты и цифры. Без воды.",
      items: [
        {
          value: "Республиканский масштаб",
          title: "Национальная ATM-программа",
          body: "Роль программного менеджера — от структурирования контракта до реализации. Имя заказчика не раскрывается.",
        },
        {
          value: "20+ лет",
          title: "Междоменный опыт",
          body: "ИТ, телекоммуникации и гражданская авиация, с опытом поставок от глобальных ATM-вендоров и интеграторов.",
        },
        {
          value: "$3M → $24M",
          title: "Развитие интеграторского бизнеса",
          body: "Помогли клиентской компании — системному интегратору — вырасти с $3M до $24M. Позже вошла в топ ИТ-компаний по версии Forbes (2019).",
        },
        {
          value: "IPMA",
          title: "Сертифицированное управление программами",
          body: "Дисциплина управления и реализации в соответствии с международными стандартами IPMA.",
        },
      ],
    },
    contact: {
      eyebrow: "Контакты",
      title: "Расскажите о своей программе.",
      subtitle:
        "Вопросы по закупкам, аудит программ или инженерная поддержка — пишите напрямую.",
      company: "ITM Consulting",
      location: "Astana, Yesil District, E 652 Street, Building 10, Premises 3",
      emailLabel: "Email",
      email: "pm@itmcon.co",
      whatsappLabel: "WhatsApp",
      whatsappCta: "Написать в WhatsApp",
      whatsappUrl: "https://wa.me/77773331234",
      note: "Работа с клиентами, как правило, регулируется NDA. Имена вендоров и партнёров сообщаются по запросу.",
    },
    footer: {
      tagline: "Независимая практика ATM и CNS.",
      rights: "Все права защищены.",
      builtIn: "Базируемся в Астане, Казахстан.",
    },
  },
};

export const DEFAULT_LANG = "en";
